/**
 * Grok (xAI) Configuration
 * 
 * xAI Grok provides advanced AI models with reasoning capabilities,
 * vision support, and specialized models for different use cases.
 * Models are prefixed with "grok/" to route through GrokProvider
 */

export const GROK_MODELS = {
  // Current flagship model
  GROK_4_5: 'grok/grok-4.5',

  // Previous generation fast models — kept selectable for parity / legacy pins
  GROK_4_1_FAST_REASONING: 'grok/grok-4-1-fast-reasoning',
  GROK_4_1_FAST_NON_REASONING: 'grok/grok-4-1-fast-non-reasoning',

  // Fast models for general use
  GROK_4_FAST_REASONING: 'grok/grok-4-fast-reasoning',
  GROK_4_FAST_NON_REASONING: 'grok/grok-4-fast-non-reasoning',
  
  // Specialized models
  GROK_CODE_FAST: 'grok/grok-code-fast-1',
  GROK_3_MINI: 'grok/grok-3-mini',
  
  // Premium models
  GROK_4_PREMIUM: 'grok/grok-4-0709',
  GROK_3: 'grok/grok-3',
  
  // Multimodal models
  GROK_2_VISION: 'grok/grok-2-vision-1212',
  GROK_IMAGINE_IMAGE: 'grok/grok-imagine-image',
  GROK_IMAGINE_IMAGE_PRO: 'grok/grok-imagine-image-pro',
  
  // Aliases for easier access
  // GROK_FAST points to the current default uncensored model.
  // Bump this when xAI releases a newer generation; every caller that wants
  // "the current Grok" reads this alias so the swap is one line.
  GROK_FAST: 'grok/grok-4.5',
  GROK_CODE: 'grok/grok-code-fast-1',
  GROK_VISION: 'grok/grok-2-vision-1212',
  GROK_MINI: 'grok/grok-3-mini',
};

export const DEFAULT_GROK_MODEL = GROK_MODELS.GROK_4_5;

export const AVAILABLE_GROK_MODELS = [
  // Flagship model — current default
  {
    id: GROK_MODELS.GROK_4_5,
    name: 'Grok 4.5',
    provider: 'grok',
    family: 'grok',
    description: "xAI's flagship model for coding, agentic tool calling, and reasoning (default)",
    capabilities: ['text', 'reasoning'],
    tier: 'premium'
  },

  // Previous-generation fast reasoning model (kept selectable for parity)
  {
    id: GROK_MODELS.GROK_4_1_FAST_REASONING,
    name: 'Grok 4.1 Fast Reasoning',
    provider: 'grok',
    family: 'grok',
    description: 'Previous generation fast model with reasoning capabilities',
    capabilities: ['text', 'reasoning'],
    tier: 'premium'
  },

  // Previous-generation fast reasoning model (kept selectable for parity)
  {
    id: GROK_MODELS.GROK_4_FAST_REASONING,
    name: 'Grok 4 Fast Reasoning',
    provider: 'grok',
    family: 'grok',
    description: 'Previous generation fast reasoning model',
    capabilities: ['text', 'reasoning'],
    tier: 'premium'
  },
  
  // Fast non-reasoning model
  { 
    id: GROK_MODELS.GROK_4_FAST_NON_REASONING, 
    name: 'Grok 4 Fast', 
    provider: 'grok',
    family: 'grok',
    description: 'Fastest general-purpose model without reasoning',
    capabilities: ['text'],
    tier: 'balanced'
  },
  
  // Code-focused model
  { 
    id: GROK_MODELS.GROK_CODE_FAST, 
    name: 'Grok Code Fast', 
    provider: 'grok',
    family: 'grok',
    description: 'Optimized for programming and code generation',
    capabilities: ['text', 'code'],
    tier: 'specialized'
  },
  
  // Mini model for quick tasks
  { 
    id: GROK_MODELS.GROK_3_MINI, 
    name: 'Grok 3 Mini', 
    provider: 'grok',
    family: 'grok',
    description: 'Lightweight model for fast responses',
    capabilities: ['text'],
    tier: 'budget'
  },
  
  // Vision model
  { 
    id: GROK_MODELS.GROK_2_VISION, 
    name: 'Grok 2 Vision', 
    provider: 'grok',
    family: 'grok',
    description: 'Vision-capable model for image understanding',
    capabilities: ['text', 'vision'],
    tier: 'multimodal'
  },
  
  // Premium models
  { 
    id: GROK_MODELS.GROK_4_PREMIUM, 
    name: 'Grok 4 Premium', 
    provider: 'grok',
    family: 'grok',
    description: 'Premium Grok 4 model with highest quality',
    capabilities: ['text', 'reasoning'],
    tier: 'premium'
  },
  
  { 
    id: GROK_MODELS.GROK_3, 
    name: 'Grok 3', 
    provider: 'grok',
    family: 'grok',
    description: 'Standard Grok 3 model',
    capabilities: ['text'],
    tier: 'balanced'
  },
  
  // Image generation models
  {
    id: GROK_MODELS.GROK_IMAGINE_IMAGE,
    name: 'Grok Imagine',
    provider: 'grok',
    family: 'grok-image',
    description: 'Image generation model ($0.02/image)',
    capabilities: ['image_generation'],
    tier: 'creative'
  },
  {
    id: GROK_MODELS.GROK_IMAGINE_IMAGE_PRO,
    name: 'Grok Imagine Pro',
    provider: 'grok',
    family: 'grok-image',
    description: 'Premium image generation model ($0.07/image)',
    capabilities: ['image_generation'],
    tier: 'creative'
  },
];

/**
 * Check if a model ID is a Grok model
 */
export function isGrokModel(modelId: string): boolean {
  return modelId.startsWith('grok/');
}

/**
 * Get Grok model name without prefix
 */
export function getGrokModelName(modelId: string): string {
  return modelId.replace('grok/', '');
}

/**
 * Check if a Grok model has reasoning capabilities
 */
export function isGrokReasoningModel(modelId: string): boolean {
  const reasoningModels = [
    GROK_MODELS.GROK_4_5,
    GROK_MODELS.GROK_4_1_FAST_REASONING,
    GROK_MODELS.GROK_4_FAST_REASONING,
    GROK_MODELS.GROK_4_PREMIUM
  ];
  return reasoningModels.includes(modelId);
}

/**
 * Check if a Grok model has vision capabilities
 */
export function isGrokVisionModel(modelId: string): boolean {
  return modelId === GROK_MODELS.GROK_2_VISION;
}

/**
 * Check if a Grok model can generate images
 */
export function isGrokImageGenerationModel(modelId: string): boolean {
  return modelId === GROK_MODELS.GROK_IMAGINE_IMAGE || modelId === GROK_MODELS.GROK_IMAGINE_IMAGE_PRO;
}

/**
 * Get the best Grok model for a specific use case
 */
export function getGrokModelForUseCase(useCase: 'reasoning' | 'code' | 'vision' | 'fast' | 'budget'): string {
  switch (useCase) {
    case 'reasoning':
      return GROK_MODELS.GROK_4_5;
    case 'code':
      return GROK_MODELS.GROK_CODE_FAST;
    case 'vision':
      return GROK_MODELS.GROK_2_VISION;
    case 'fast':
      return GROK_MODELS.GROK_4_FAST_NON_REASONING;
    case 'budget':
      return GROK_MODELS.GROK_3_MINI;
    default:
      return DEFAULT_GROK_MODEL;
  }
}
